import type {
  Ambientes,
  Atelie,
  Contato,
  Depoimento,
  Hero,
  Processo,
  Showroom,
} from '@plane/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  fetchAmbientes,
  fetchAtelie,
  fetchContato,
  fetchDepoimento,
  fetchHero,
  fetchProcesso,
  fetchShowroom,
  saveAmbientes,
  saveAtelie,
  saveContato,
  saveDepoimento,
  saveHero,
  saveProcesso,
  saveShowroom,
} from '@/modules/site-content/services/site-content.service';

const chave = (secao: string) => ['site-content', secao] as const;

function useSecao<T>(secao: string, buscar: () => Promise<T | null>) {
  return useQuery({ queryKey: chave(secao), queryFn: buscar });
}

function useSalvarSecao<T>(secao: string, salvar: (conteudo: T) => Promise<T>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: salvar,
    // Grava a resposta no cache em vez de invalidar: a API devolve exatamente o
    // que ficou salvo, então uma segunda consulta não acrescentaria nada.
    onSuccess: (salvo) => queryClient.setQueryData(chave(secao), salvo),
  });
}

export const useAmbientes = () => useSecao('ambientes', fetchAmbientes);
export const useSaveAmbientes = () => useSalvarSecao<Ambientes>('ambientes', saveAmbientes);

export const useAtelie = () => useSecao('atelie', fetchAtelie);
export const useSaveAtelie = () => useSalvarSecao<Atelie>('atelie', saveAtelie);

export const useContato = () => useSecao('contato', fetchContato);
export const useSaveContato = () => useSalvarSecao<Contato>('contato', saveContato);

export const useDepoimento = () => useSecao('depoimento', fetchDepoimento);
export const useSaveDepoimento = () => useSalvarSecao<Depoimento>('depoimento', saveDepoimento);

export const useHero = () => useSecao('hero', fetchHero);
export const useSaveHero = () => useSalvarSecao<Hero>('hero', saveHero);

export const useShowroom = () => useSecao('showroom', fetchShowroom);
export const useSaveShowroom = () => useSalvarSecao<Showroom>('showroom', saveShowroom);

export const useProcesso = () => useSecao('processo', fetchProcesso);
export const useSaveProcesso = () => useSalvarSecao<Processo>('processo', saveProcesso);
